/** Corporate design tokens for candidate profile & staff forms — PaceHRM DS theme. */

export const CANDIDATE_FORM_MODAL_WIDTH = "max-w-7xl";
export const CANDIDATE_FORM_GRID = "grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3";
/** Salary & Experience — 4 fields in a 4-column grid on wide layouts */
export const CANDIDATE_SALARY_GRID =
  "grid gap-2.5 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-start";
export const CANDIDATE_FORM_ROW_GRID = "grid gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5";
/** Education row: degree 2× width, institution 2×, year / field / gpa single */
export const CANDIDATE_EDUCATION_ROW_GRID =
  "grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 xl:grid-cols-7 items-start";
export const CANDIDATE_EDU_DEGREE_CELL =
  "field-with-label min-w-0 sm:col-span-2 lg:col-span-2 xl:col-span-2";
export const CANDIDATE_EDU_INSTITUTION_CELL =
  "field-with-label min-w-0 sm:col-span-2 lg:col-span-2 xl:col-span-2";
export const CANDIDATE_EDU_SMALL_CELL = "field-with-label min-w-0 lg:col-span-1 xl:col-span-1";
export const CANDIDATE_TOGGLE_GRID = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5";
/** Work preferences: 2×2 top (modes + channels), locations full width below */
export const CANDIDATE_WORK_PREFS_GRID = "grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch";
/** Profile heading (left) + area of expertise (right) */
export const CANDIDATE_HEADING_EXPERTISE_GRID =
  "grid gap-2.5 grid-cols-1 sm:grid-cols-2 items-start";

/** Selected chips — outline theme (matches product primary buttons) */
export const CANDIDATE_CHIP_SELECTED =
  "candidate-selected-chip border-2 border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))] shadow-sm font-semibold";
export const CANDIDATE_CHIP_SELECTED_BORDER = CANDIDATE_CHIP_SELECTED;
export const CANDIDATE_CHIP_SELECTED_REMOVE_HOVER =
  "hover:bg-[hsl(var(--ds-primary)/0.16)] hover:text-[hsl(var(--ds-primary-hover))] text-[hsl(var(--ds-primary))]";

/** Unified category headings */
export const CANDIDATE_SECTION_HEADING =
  "text-sm font-bold text-[hsl(var(--ds-fg))] tracking-tight";
export const CANDIDATE_SECTION_HEADER =
  "flex flex-wrap items-center justify-between gap-2 pb-2 mb-1 border-b border-[hsl(var(--ds-border))]";

export const CORPORATE = {
  panel: "rounded-2xl",
  card:
    "rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] overflow-hidden",
  cardHeader:
    "flex items-center justify-between gap-2 px-3 py-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]",
  cardTitle: "text-sm font-bold text-[hsl(var(--ds-fg))] tracking-tight",
  cardSubtitle: "text-[10px] text-[hsl(var(--ds-muted-fg))] mt-0.5",
  cardBody: "p-3",
  input:
    "border border-[hsl(var(--ds-border))] px-2.5 py-1.5 text-xs w-full bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] rounded-lg shadow-sm transition-colors hover:border-[hsl(var(--ds-primary)/0.45)] focus:outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]",
  inputError:
    "border border-[hsl(var(--ds-danger)/0.45)] px-2.5 py-1.5 text-xs w-full bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] rounded-lg shadow-sm focus:outline-none focus:border-[hsl(var(--ds-danger))] focus:ring-2 focus:ring-[hsl(var(--ds-danger)/0.25)]",
  select:
    "border border-[hsl(var(--ds-border))] px-2.5 py-1.5 text-xs w-full bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] rounded-lg shadow-sm focus:outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]",
  label: "ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]",
  chip: "inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-medium rounded-full",
  chipRemove:
    "hover:opacity-80 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] font-bold leading-none",
  searchInput:
    "border border-[hsl(var(--ds-border))] px-2.5 py-1.5 text-xs w-full bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] rounded-lg shadow-sm focus:outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]",
  menu:
    "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)] rounded-lg overflow-hidden text-xs",
  menuItem:
    "w-full text-left px-2.5 py-2 text-xs text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] transition-colors",
  menuItemCreate:
    "w-full text-left px-2.5 py-2 text-xs text-[hsl(var(--ds-primary))] font-medium hover:bg-[hsl(var(--ds-primary)/0.08)] border-t border-[hsl(var(--ds-border))]",
  /** Same size as location/skill chips (TagInputPicker selected badges) */
  toggle:
    "candidate-pref-toggle inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border rounded-full transition-all duration-150 leading-tight whitespace-nowrap shrink-0 w-auto max-w-full no-portal-round",
  toggleOn: `${CANDIDATE_CHIP_SELECTED} ${CANDIDATE_CHIP_SELECTED_BORDER}`,
  toggleOff:
    "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] border border-[hsl(var(--ds-border))] hover:border-[hsl(var(--ds-primary)/0.45)] hover:bg-[hsl(var(--ds-primary)/0.06)]",
  btnSecondary:
    "px-3 py-1.5 text-xs font-medium border border-[hsl(var(--ds-border))] rounded-lg bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] shadow-sm transition-colors",
  btnPrimary:
    "px-3 py-1.5 text-xs font-medium rounded-lg border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] shadow-sm disabled:opacity-50 transition-colors",
  nestedCard:
    "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2",
  badge:
    "inline-flex items-center justify-center min-w-[1.125rem] h-5 px-1.5 text-[10px] font-bold rounded-full",
};

export const CANDIDATE_INPUT_CLASS = CORPORATE.input;
export const CANDIDATE_INPUT_ERROR_CLASS = CORPORATE.inputError;
export const CANDIDATE_SELECT_CLASS = CORPORATE.select;
/** Neutral section shell (filter modal, etc.) */
export const CANDIDATE_SECTION_CLASS = `${CORPORATE.card} p-3 space-y-2.5`;

const CANDIDATE_SECTION_SHELL =
  "candidate-form-section rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] overflow-hidden p-4 space-y-2.5";

/** White surface cards — no tinted fills. */
export function candidateSectionClass(_index = 0) {
  return CANDIDATE_SECTION_SHELL;
}

export const CANDIDATE_PROFILE_BADGE =
  "inline-flex items-center px-2.5 py-0.5 text-[11px] font-semibold rounded-full border border-[hsl(var(--ds-border))] shadow-sm";
/** @deprecated Use CandidateSectionHeader + CANDIDATE_SECTION_HEADING */
export const CANDIDATE_SECTION_TITLE = CANDIDATE_SECTION_HEADING;
export const CANDIDATE_TOGGLE_BTN = CORPORATE.toggle;
export const CANDIDATE_TOGGLE_BTN_ON = CORPORATE.toggleOn;
export const CANDIDATE_TOGGLE_BTN_OFF = CORPORATE.toggleOff;
export const CANDIDATE_MODAL_FOOTER_BTN = CORPORATE.btnSecondary;
export const CANDIDATE_MODAL_PRIMARY_BTN = CORPORATE.btnPrimary;

/** @deprecated Filter modal uses CANDIDATE_SECTION_CLASS + CandidateSectionHeader */
export const FILTER_CORPORATE = {
  section: CANDIDATE_SECTION_CLASS,
  sectionHeader: CANDIDATE_SECTION_HEADING,
  sectionBody: "grid gap-4 md:grid-cols-2",
  fieldLabel:
    "ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]",
};
